import { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { api, fmtMoney, fmtNum } from "@/lib/api";
import { Panel, Spinner } from "@/components/common";
import { TrimBlocks } from "@/components/dashboard/TrimBlocks";

const ROWS = [["Spend", (b) => fmtMoney(b.cost)], ["Impressions", (b) => fmtNum(b.impressions)], ["Clicks", (b) => fmtNum(b.clicks)],
  ["CTR", (b) => (b.ctr == null ? "—" : `${b.ctr}%`)], ["CPC", (b) => fmtMoney(b.cpc)], ["Leads (form / WhatsApp / call)", (b) => `${b.leads} (${b.form} / ${b.whatsapp} / ${b.call})`],
  ["Cost per lead", (b) => fmtMoney(b.cost_per_lead)], ["Bookings", (b) => b.booked], ["Booked + package value", (b) => fmtMoney(b.value)]];

const HourBars = ({ hours }) => {
  const maxC = Math.max(1, ...hours.map((h) => h.cost));
  const maxL = Math.max(1, ...hours.map((h) => h.leads));
  return (
    <div className="mt-5" data-testid="schedule-hour-bars">
      <div className="cc-eyebrow mb-2">By hour of day (SGT) · <span className="text-emerald-300">spend</span> · <span className="text-cyan-300">leads</span></div>
      <div className="flex h-24 items-end gap-[3px]">
        {hours.map((h) => (
          <div key={h.hour} className="flex h-full flex-1 items-end gap-[1px]" title={`${h.hour}:00 · ${fmtMoney(h.cost)} · ${h.leads} leads`} data-testid={`schedule-hour-${h.hour}`}>
            <div className="flex-1 rounded-t bg-emerald-500/70 transition-[height] duration-500" style={{ height: `${(h.cost / maxC) * 100}%` }} />
            <div className="flex-1 rounded-t bg-cyan-400/70 transition-[height] duration-500" style={{ height: `${(h.leads / maxL) * 100}%` }} />
          </div>
        ))}
      </div>
      <div className="mt-1 flex justify-between font-mono text-[10px] text-slate-500"><span>0h</span><span>6h</span><span>12h</span><span>18h</span><span>23h</span></div>
    </div>
  );
};

export const ScheduleCompare = () => {
  const [range, setRange] = useState("since_change");
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get("/reports/schedule-compare", { params: { range } }).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, [range]);
  const tabs = [["since_change", "Since schedule change"], ["7d", "Last 7 days"]];
  return (
    <Panel title="Extra hours vs clinic hours" icon={Clock} testid="schedule-compare" right={<div className="flex flex-wrap gap-2">{tabs.map(([k, l]) => <button key={k} data-testid={`schedule-range-${k}`} onClick={() => setRange(k)} className={`cc-btn px-3 py-1 text-xs ${range === k ? "cc-btn-primary" : "cc-btn-ghost"}`}>{l}</button>)}</div>}>
      {!d ? <Spinner /> : d.error ? <div className="text-sm text-slate-400">Could not load the comparison.</div> : (
        <>
          <div className="mb-3 text-xs text-slate-400" data-testid="schedule-scope">{d.scope} · {d.dates[0]} to {d.dates[1]} · clinic hours {d.clinic_hours.map(([a, b]) => `${a} ${b}`).join(", ")}</div>
          {d.timezone_warning && <div className="mb-3 text-xs text-amber-300" data-testid="schedule-tz-warning">{d.timezone_warning}</div>}
          <div className="overflow-x-auto"><table className="w-full text-left text-xs" data-testid="schedule-compare-table">
            <thead className="text-slate-500"><tr><th className="py-1.5 font-medium" /><th className="py-1.5 font-medium">Clinic hours</th><th className="py-1.5 font-medium">Extra hours</th></tr></thead>
            <tbody>{ROWS.map(([label, f]) => (
              <tr key={label} className="border-t border-[#1e293b] text-slate-300"><td className="py-1.5 text-slate-500">{label}</td>
                <td className="py-1.5 font-mono" data-testid={`schedule-clinic-${label}`}>{f(d.buckets.clinic)}</td><td className="py-1.5 font-mono" data-testid={`schedule-extra-${label}`}>{f(d.buckets.extra)}</td></tr>
            ))}</tbody>
          </table></div>
          <div className={`mt-4 rounded-md p-3 text-sm ${d.enough_data ? "bg-emerald-500/10 text-emerald-200" : "bg-slate-500/10 text-slate-400"}`} data-testid="schedule-verdict">
            {d.verdict}{!d.enough_data && ` (needs ${d.min_clicks}+ clicks in each bucket)`}</div>
          <HourBars hours={d.per_hour} />
          <TrimBlocks />
          <p className="mt-3 text-[11px] text-slate-500" data-testid="schedule-note">{d.note} {d.lead_time_basis}</p>
        </>
      )}
    </Panel>
  );
};
